import { InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import { upperCase } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AdditionalServiceInfoModal from 'src/app/AddressBook/Modals/AdditionalServiceInfoModal';
import ServiceLabel from 'src/app/MyQuote/BookingStepper/components/ServiceLabel';
import CheckBox from 'src/components/CheckBox';
import InputText from 'src/components/InputText/InputText';
import TooltipCmp from 'src/components/TooltipCmp';
import { MEASUREMENT, QUOTE_CLASSIFICATION } from 'src/constants/common';
import { selectFormData, selectIsHiddenFieldInBooking, setQuoteBookingFormData } from 'src/redux/bookingStepper.slice';
import { RootState } from 'src/redux/store';

import LineItem, { initDimensionData } from './LineItem';

interface IDimensionsProps {
    stepperFooterRef: React.RefObject<any>;
}

export interface IDimensionsForm {
    totalWeight: string | number | null;
    linearFootage: string | number | null;
    weightMeasurement: string;
    lengthMeasurement: string;
    dimensions?: any[];
}

const Dimensions: React.FC<IDimensionsProps> = ({ stepperFooterRef }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const formData = useSelector(selectFormData);
    const { serviceTypes } = useSelector((state: RootState) => state.basic);
    const isHiddenFieldInBooking = useSelector(selectIsHiddenFieldInBooking);
    // Get service type from orderDetails
    const serviceType = formData?.orderDetails?.orderType || '';
    const isLTL = serviceType?.toLowerCase() === 'ltl';
    const [isShowAdditionalServiceInfoModal, setIsShowAdditionalServiceInfoModal] = useState(false);
    const [selectedServiceType, setSelectedServiceType] = useState<string>('');

    // Initialize form with values from Redux store
    const methods = useForm<IDimensionsForm>({
        defaultValues: {
            totalWeight: formData?.loadDetails?.totalWeight || '',
            linearFootage: !isLTL ? '53' : formData?.loadDetails?.linearFootage || '',
            weightMeasurement: upperCase(formData?.loadDetails?.weightMeasurement) || MEASUREMENT.WEIGHT1.value,
            lengthMeasurement: upperCase(formData?.loadDetails?.lengthMeasurement) || MEASUREMENT.LENGTH1.value,
            dimensions: isLTL ? (formData?.loadDetails?.dimensions?.length > 0 ? formData?.loadDetails?.dimensions : [initDimensionData]) : formData?.loadDetails?.dimensions || [],
        },
    });

    const { control, watch, reset, trigger, getValues } = methods;

    // Reset form when formData changes
    useEffect(() => {
        if (formData?.loadDetails) {
            reset({
                totalWeight: formData.loadDetails.totalWeight || '',
                linearFootage: !isLTL ? '53' : formData.loadDetails.linearFootage || '',
                weightMeasurement: upperCase(formData.loadDetails.weightMeasurement) || MEASUREMENT.WEIGHT1.value,
                lengthMeasurement: upperCase(formData.loadDetails.lengthMeasurement) || MEASUREMENT.LENGTH1.value,
                dimensions: isLTL ? (formData.loadDetails.dimensions?.length > 0 ? formData.loadDetails.dimensions : [initDimensionData]) : formData.loadDetails.dimensions || [],
            });
        }
    }, [formData?.loadDetails, reset]);

    const handleNext = useCallback(async () => {
        try {
            // Validate form fields
            const isValid = await trigger();

            if (!isValid) {
                throw new Error('Please fill in all required fields');
            }

            // Get form values and submit
            const formValues = getValues();
            const submitData = {
                loadDetails: {
                    totalWeight: formValues.totalWeight || '',
                    linearFootage: formValues.linearFootage || '',
                    weightMeasurement: formValues.weightMeasurement,
                    lengthMeasurement: formValues.lengthMeasurement,
                    dimensions: formValues.dimensions || [],
                },
            };

            dispatch(setQuoteBookingFormData(submitData));

            await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error: any) {
            console.error('Error in handleNext:', error);
            throw error;
        }
    }, [trigger, getValues, dispatch]);

    const handleBack = useCallback(() => {
        // Save current state to Redux before going back
        const formValues = getValues();
        const submitData = {
            loadDetails: {
                totalWeight: formValues.totalWeight || '',
                linearFootage: formValues.linearFootage || '',
                weightMeasurement: formValues.weightMeasurement,
                lengthMeasurement: formValues.lengthMeasurement,
                dimensions: formValues.dimensions || [],
            },
        };

        dispatch(setQuoteBookingFormData(submitData));
    }, [getValues, dispatch]);

    const watchServiceId: number[] = formData?.services?.serviceId ? formData.services.serviceId.split(',').filter(Boolean).map(Number) : [];
    const watchWeightMeasurement = watch('weightMeasurement');

    const textReplacements = [
        {
            original: 'After Hours / Weekend / Holiday Pickup',
            replacement: t('QuotingHub.Common.After_Hours_Pickup'),
        },
        {
            original: 'Pickup Appointment Required',
            replacement: t('QuotingHub.Common.Pickup_Appointment'),
        },
        {
            original: 'Delivery Appointment Required',
            replacement: t('QuotingHub.Common.Delivery_Appointment'),
        },
    ];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements?.find((item) => item?.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const getList = useCallback(
        (data: any, excludedServices: string[] = []) => (
            <>
                {data.map((val: any) =>
                    (val?.type !== 'OTHER' || serviceType === QUOTE_CLASSIFICATION.FTL || val?.slug === 'load_insurance' || (serviceType !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null)) &&
                    !excludedServices.includes(val.slug) ? (
                        <div className="flex w-[calc(33.33%_-_8px)]" key={`service_${val.id}`}>
                            <CheckBox
                                id={`service_${val.id}`}
                                onChangeFunc={() => {}}
                                name={val.slug}
                                value={val.id}
                                checked={watchServiceId.includes(val.id) || false}
                                parentClassName="mt-px"
                                disabled={true}
                            />
                            <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-2 flex-1">
                                {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                    <TooltipCmp message={val.name} childrenClassName="!flex">
                                        <label htmlFor={`service_${val.id}`} className="truncate leading-4 text-textSecondary text-xs font-medium cursor-not-allowed opacity-50">
                                            {getReplacedText(val.name)}
                                        </label>
                                    </TooltipCmp>
                                ) : (
                                    <ServiceLabel
                                        htmlFor={`service_${val.id}`}
                                        name={val.name}
                                        displayName={getReplacedText(val.name)}
                                        labelClassName="leading-4 text-textSecondary text-xs font-medium cursor-not-allowed opacity-50"
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        ''
                    )
                )}
            </>
        ),
        [watchServiceId, serviceType]
    );

    // Register handlers with parent via the ref
    useEffect(() => {
        const stepperHandlers = {
            triggerNext: async () => {
                await handleNext();
            },
            triggerBack: async () => {
                await handleBack();
            },
        };

        if (stepperFooterRef && 'current' in stepperFooterRef) {
            (stepperFooterRef as any).current = stepperHandlers;
        }
    }, [stepperFooterRef, handleNext, handleBack]);

    return (
        <FormProvider {...methods}>
            {/* Load Overview */}
            <div className="animate-field-item">
                <div className="stepper-content-container">
                    <div className="stepper-lable-wrap">
                        <label className="stepper-label-common">
                            {t('Common.Load_Overview')}
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </label>
                        <p className="text-textSecondary text-xs font-normal">{t('Common.Auto_Calculated')}</p>
                    </div>
                    <div className="flex items-center gap-x-2 flex-1">
                        <Controller
                            control={control}
                            name="totalWeight"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    inputName="totalWeight"
                                    placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                    parentClassName="flex-1"
                                    value={value || ''}
                                    onChangeFunc={onChange}
                                    inputType="number"
                                    isAllowDecimal={true}
                                    required
                                    shouldFormatNumber={true}
                                    maxLength={7}
                                    isError={!!error}
                                    inputValueUnitType={watchWeightMeasurement?.toLowerCase()}
                                    disabled={isHiddenFieldInBooking}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="linearFootage"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    inputName="linearFootage"
                                    placeholder={t('QuotingHub.Common.Enter_linear_footage')}
                                    parentClassName="flex-1"
                                    value={value || ''}
                                    onChangeFunc={onChange}
                                    required
                                    shouldFormatNumber={true}
                                    maxLength={2}
                                    inputValueUnitType="ft"
                                    inputType="number"
                                    isAllowDecimal={false}
                                    isError={!!error}
                                    disabled={isHiddenFieldInBooking}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            {isLTL && (
                <>
                    <div className="border-t border-utilityGray200 stepper-content-container !mb-5">
                        <div className="stepper-lable-wrap">
                            <label className="stepper-label-common">{t('Common.Load_Details')}</label>
                        </div>
                    </div>
                    <LineItem />
                </>
            )}

            {/* Other Additional Services */}
            <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        {t('QuotingHub.Common.Other_Additional_Services')}
                        <div
                            className={`cursor-pointer`}
                            onClick={() => {
                                setSelectedServiceType('OTHER');
                                setIsShowAdditionalServiceInfoModal(true);
                            }}
                        >
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1 space-y-5">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.OTHER || [], ['load_insurance'])}</div>
                </div>
            </div>

            {/* Additional Service Info Modal */}
            {isShowAdditionalServiceInfoModal && (
                <AdditionalServiceInfoModal
                    handleClose={() => setIsShowAdditionalServiceInfoModal(false)}
                    services={
                        selectedServiceType === 'PICKUP'
                            ? serviceTypes?.PICKUP || []
                            : selectedServiceType === 'DELIVERY'
                            ? serviceTypes?.DELIVERY || []
                            : (serviceTypes?.OTHER || []).filter((s: any) => s.slug !== 'load_insurance')
                    }
                    title={
                        selectedServiceType === 'PICKUP'
                            ? t('QuotingHub.Common.Pickup_Additional_Services')
                            : selectedServiceType === 'DELIVERY'
                            ? t('QuotingHub.Common.Delivery_Additional_Services')
                            : t('QuotingHub.Common.Other_Additional_Services')
                    }
                />
            )}
        </FormProvider>
    );
};

export default Dimensions;
